import React from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutGrid, CalendarCheck, CreditCard, User, ConciergeBell, LogOut, Home, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/brand";
import GhostLauncher from "@/components/ghost/GhostLauncher";
import AmbientSound from "@/components/AmbientSound";
import { ContactLine } from "@/components/site/ContactLine";

const LINKS = [
  { to: "/portal", label: "Overview", icon: LayoutGrid, end: true },
  { to: "/portal/reservations", label: "Reservations", icon: CalendarCheck },
  { to: "/portal/concierge", label: "Concierge", icon: ConciergeBell },
  { to: "/portal/post-stay", label: "Post-Stay", icon: Sparkles },
  { to: "/portal/card", label: "Membership Card", icon: CreditCard },
  { to: "/portal/profile", label: "Profile", icon: User },
];

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-ink flex">
      <aside className="w-64 shrink-0 border-r border-gold/15 bg-surface/40 hidden md:flex flex-col p-5 fixed h-full">
        <Link to="/" className="mb-8"><LogoMark className="h-10 w-10" withText size="text-lg" /></Link>
        <nav className="space-y-1 flex-1">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`portal-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${isActive ? "bg-gold/10 text-gold" : "text-muted-foreground hover:text-foreground hover:bg-white/5"}`}>
              <l.icon size={17} /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="space-y-1 pt-4 border-t border-gold/15">
          <button onClick={() => nav("/")} className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:text-foreground w-full"><Home size={17} /> Public site</button>
          <button onClick={() => { logout(); nav("/"); }} data-testid="portal-logout" className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:text-red-300 w-full"><LogOut size={17} /> Sign out</button>
        </div>
      </aside>
      <div className="flex-1 md:ml-64">
        <header className="md:hidden glass-dark p-4 flex justify-between items-center sticky top-0 z-30">
          <LogoMark className="h-9 w-9" withText size="text-base" />
          <button onClick={() => { logout(); nav("/"); }} className="text-muted-foreground"><LogOut size={18} /></button>
        </header>
        <div className="md:hidden flex overflow-x-auto gap-2 p-3 border-b border-gold/15">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `whitespace-nowrap px-3 py-1.5 rounded-full text-xs ${isActive ? "bg-gold text-ink" : "text-muted-foreground border border-white/10"}`}>{l.label}</NavLink>
          ))}
        </div>
        <main id="main-content" className="p-6 lg:p-10 max-w-5xl">
          <div className="mb-8">
            <p className="text-xs tracking-label uppercase text-pool">Welcome back</p>
            <h1 className="font-serif text-4xl mt-1">{user?.first_name} {user?.last_name}</h1>
          </div>
          <Outlet />
        </main>
        <footer className="px-6 lg:px-10 py-8 border-t border-gold/15 mt-10 text-center" data-testid="portal-footer">
          <div className="font-serif text-gold tracking-brand">THE D</div>
          <div className="text-[10px] tracking-label uppercase text-muted-foreground mt-1">Private Resort &amp; Social House</div>
          <p className="font-serif italic text-gold/90 mt-3 text-sm">NEVER FORGET THE DIFFERENCE YOU MAKE</p>
          <p className="text-xs text-muted-foreground mt-3">Guest assistance: <ContactLine /></p>
          <p className="text-xs text-muted-foreground mt-2">Site built and managed by <span className="text-foreground/80">Prestigious Companies</span>.</p>
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} The D. All rights reserved.</p>
        </footer>
      </div>
      <AmbientSound />
      <GhostLauncher mode="guest" />
    </div>
  );
}
